import { format, parseISO, isValid } from 'date-fns';
import { CURRENCY_SYMBOL, DATE_DISPLAY_FORMAT, MONEY_SCALE } from '@/domain';

/**
 * Display formatting.
 *
 * Money arrives from the API as a string and stays a string. These helpers format for
 * display only — no arithmetic happens on the client, because every total is computed and
 * persisted server-side (FR-CALC-007).
 */

/** 125000 -> ₹1,25,000.00 (FRD §2.3, Indian digit grouping). */
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return `${CURRENCY_SYMBOL}0.00`;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return `${CURRENCY_SYMBOL}0.00`;

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: MONEY_SCALE,
    maximumFractionDigits: MONEY_SCALE,
  }).format(numeric);
}

/** The same grouping without the symbol — for table columns with a currency header. */
export function formatMoneyPlain(value: string | number | null | undefined): string {
  return formatMoney(value).replace(CURRENCY_SYMBOL, '').trim();
}

/** ISO date -> 13/08/2026. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (!isValid(date)) return '—';
  return format(date, DATE_DISPLAY_FORMAT);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (!isValid(date)) return '—';
  return format(date, `${DATE_DISPLAY_FORMAT} HH:mm`);
}

/** Turns an enum-ish value into a readable label: PARTIALLY_PAID -> Partially Paid. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** "3 days overdue" / "in 12 days" — used on dues and retention screens. */
export function formatRelativeDays(days: number): string {
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  const overdue = Math.abs(days);
  return `${overdue} day${overdue === 1 ? '' : 's'} overdue`;
}
